// =============================================================================
// ACT MATH TEST BANK
// Form J08 (October 2025) - RELEASED ACT TEST
// =============================================================================
//
// SOURCE: Official ACT "My Answer Key" booklet
// TYPE: Released test (actual administered exam)
// USE: TESTING MODE ONLY (pristine questions for assessment)
//
// FORMAT: Enhanced ACT
//   - 45 total questions administered, 41 scored
//   - 4 field test questions removed from this booklet
//   - This file contains all 41 scored questions
//
// =============================================================================

export const FORM_METADATA = {
  formId: 'J08',
  administrationDate: 'October 2025',
  testType: 'Released ACT',
  format: 'Enhanced',
  totalQuestions: 41,
  scoredQuestions: 41,
  timeMinutes: 50,
  source: 'ACT My Answer Key Booklet'
};

// Official Scale Score Conversion (Form J08)
export const SCALE_SCORE_TABLE_J08 = [
  { raw: 41, scale: 36 },
  { raw: 40, scale: 35 }, { raw: 39, scale: 35 },
  { raw: 38, scale: 34 }, { raw: 37, scale: 34 },
  { raw: 36, scale: 33 },
  { raw: 35, scale: 32 },
  { raw: 34, scale: 31 }, { raw: 33, scale: 31 },
  { raw: 32, scale: 30 },
  { raw: 31, scale: 29 },
  { raw: 30, scale: 28 }, { raw: 29, scale: 28 },
  { raw: 28, scale: 27 },
  { raw: 27, scale: 26 }, { raw: 26, scale: 26 },
  { raw: 25, scale: 25 },
  { raw: 24, scale: 24 }, { raw: 23, scale: 24 },
  { raw: 22, scale: 23 },
  { raw: 21, scale: 22 },
  { raw: 20, scale: 21 },
  { raw: 19, scale: 20 },
  { raw: 18, scale: 19 },
  { raw: 17, scale: 18 }, { raw: 16, scale: 18 },
  { raw: 15, scale: 17 }, { raw: 14, scale: 17 },
  { raw: 13, scale: 16 }, { raw: 12, scale: 16 },
  { raw: 11, scale: 15 }, { raw: 10, scale: 15 },
  { raw: 9, scale: 14 }, { raw: 8, scale: 14 },
  { raw: 7, scale: 13 },
  { raw: 6, scale: 12 },
  { raw: 5, scale: 11 },
  { raw: 4, scale: 10 },
  { raw: 3, scale: 9 },
  { raw: 2, scale: 7 },
  { raw: 1, scale: 4 },
  { raw: 0, scale: 1 }
];

// =============================================================================
// COMPLETE TEST BANK - FORM J08 (41 Scored Questions)
// =============================================================================

export const questions_J08 = [
  // -------------------------------------------------------------------------
  // QUESTION 1 - Radical equation
  // -------------------------------------------------------------------------
  {
    id: 'J08-01',
    formId: 'J08',
    questionNumber: 1,
    categories: ['A'],
    difficulty: 1,
    topics: ['radicals', 'solving-equations'],
    question: 'What is the solution of the equation √x − 9 = 8?',
    choices: ['1', '25', '34', '289'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: '√x − 9 = 8. Add 9: √x = 17. Square both sides: x = 289.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 2 - Negative fractional exponent
  // -------------------------------------------------------------------------
  {
    id: 'J08-02',
    formId: 'J08',
    questionNumber: 2,
    categories: ['N'],
    difficulty: 2,
    topics: ['exponents', 'fractions'],
    question: '(1/64)^(−1/2) = ?',
    choices: ['−1/8', '−1/128', '8', '128'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '(1/64)^(−1/2) = (64)^(1/2) = √64 = 8. Negative exponent flips the fraction, then take square root.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 3 - Repeating decimals
  // -------------------------------------------------------------------------
  {
    id: 'J08-03',
    formId: 'J08',
    questionNumber: 3,
    categories: ['IES'],
    difficulty: 1,
    topics: ['fractions', 'decimals', 'number-properties'],
    question: 'For which of the following fractions does the decimal expansion require 1 repeating nonzero digit?',
    choices: ['1/4', '3/5', '5/8', '2/3'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: '1/4 = 0.25 (terminating). 3/5 = 0.6 (terminating). 5/8 = 0.625 (terminating). 2/3 = 0.666... = 0.6̄ (one repeating digit).',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 4 - Statistical evidence / sample size
  // -------------------------------------------------------------------------
  {
    id: 'J08-04',
    formId: 'J08',
    questionNumber: 4,
    categories: ['S'],
    difficulty: 2,
    topics: ['probability-basic', 'experimental-probability'],
    question: 'When a fair coin is flipped, heads and tails should be approximately equal. Which result provides the strongest evidence that a coin is NOT fair?',
    choices: ['2 out of 3 flips were heads', '3 out of 4 flips were heads', '4 out of 5 flips were heads', '5 out of 6 flips were heads'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Larger sample size with consistent deviation provides stronger evidence. 5/6 = 83.3% heads with 6 trials is more convincing than smaller samples.',
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 5 - Linear inequality word problem
  // -------------------------------------------------------------------------
  {
    id: 'J08-05',
    formId: 'J08',
    questionNumber: 5,
    categories: ['F'],
    difficulty: 1,
    topics: ['linear-equations', 'inequalities', 'word-problems'],
    question: 'Ana plans to save $200 for a summer skate park pass. She already has $45 and plans to save $15 per week. What is the minimum number of weeks after which Ana will have enough money?',
    choices: ['10', '11', '13', '14'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: '45 + 15w ≥ 200. 15w ≥ 155. w ≥ 10.33. Minimum whole weeks = 11.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 6 - Similar triangles property
  // -------------------------------------------------------------------------
  {
    id: 'J08-06',
    formId: 'J08',
    questionNumber: 6,
    categories: ['G'],
    difficulty: 1,
    topics: ['similar-triangles'],
    question: 'Similar triangles MUST have the same:',
    choices: ['angle measures', 'area', 'perimeter', 'side lengths'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Similar triangles have equal corresponding angles. They have proportional (not equal) sides, and different areas/perimeters unless congruent.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 7 - Squaring binomial
  // -------------------------------------------------------------------------
  {
    id: 'J08-07',
    formId: 'J08',
    questionNumber: 7,
    categories: ['A'],
    difficulty: 1,
    topics: ['foil', 'polynomial-expansion'],
    question: 'Which expression is equivalent to (3x − 9)²?',
    choices: ['6x − 18', '6x² + 81', '9x² − 54x + 81', '9x² − 27x + 18'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '(3x − 9)² = (3x)² − 2(3x)(9) + 9² = 9x² − 54x + 81.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 8 - Ratio word problem
  // -------------------------------------------------------------------------
  {
    id: 'J08-08',
    formId: 'J08',
    questionNumber: 8,
    categories: ['A'],
    difficulty: 2,
    topics: ['ratios', 'proportions', 'word-problems'],
    question: '3,432 students will attend either East High or West High. Ratio of East to West must be 11:13. How many will attend East High?',
    choices: ['143', '1,573', '2,904', '4,056'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Total parts = 11 + 13 = 24. Each part = 3432/24 = 143. East = 11 × 143 = 1,573.',
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 9 - Overtime wage expression
  // -------------------------------------------------------------------------
  {
    id: 'J08-09',
    formId: 'J08',
    questionNumber: 9,
    categories: ['A'],
    difficulty: 2,
    topics: ['linear-equations', 'word-problems'],
    question: 'Worker earns $20.10/hour for first 40 hours, 1½ times that for overtime. Which expression gives earnings for 54 hours?',
    choices: ['54(20.10 + ½(20.10))', '54(20.10 + 1½(20.10))', '40(20.10) + 14(½)(20.10)', '40(20.10) + 14(1½)(20.10)'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Regular pay: 40 × 20.10. Overtime: 14 hours × 1.5 × 20.10. Total = 40(20.10) + 14(1½)(20.10).',
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 10 - Complex number equation
  // -------------------------------------------------------------------------
  {
    id: 'J08-10',
    formId: 'J08',
    questionNumber: 10,
    categories: ['N'],
    difficulty: 2,
    topics: ['complex-numbers'],
    question: 'For complex variable z, one solution of z² = −9 is:',
    choices: ['−3', '3', '3i', '9i'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'z² = −9. z = ±√(−9) = ±3i. So 3i is a solution.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 11 - Line of best fit adjustment
  // -------------------------------------------------------------------------
  {
    id: 'J08-11',
    formId: 'J08',
    questionNumber: 11,
    categories: ['IES'],
    difficulty: 2,
    topics: ['linear-equations', 'scatterplot'],
    question: 'Student modeled data with y = 0.25x + 25. Looking at scatterplot, which change will most improve the fit?',
    choices: ['Increase slope by 0.75', 'Decrease slope by 0.25', 'Increase y-intercept by 12.5', 'Decrease y-intercept by 12.5'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'From the scatterplot, the data points appear to be above the line, suggesting the y-intercept should be increased.',
    hasFigure: true,
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 12 - Conditional probability
  // -------------------------------------------------------------------------
  {
    id: 'J08-12',
    formId: 'J08',
    questionNumber: 12,
    categories: ['S'],
    difficulty: 2,
    topics: ['probability-conditional'],
    question: 'Committee has 11 juniors and 79 seniors. Two different members selected for leadership. Given 1st selected is junior, probability 2nd is senior?',
    choices: ['10/79', '10/89', '79/89', '79/90'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'After selecting 1 junior, 89 members remain (10 juniors, 79 seniors). P(senior) = 79/89.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 13 - Linear model residual
  // -------------------------------------------------------------------------
  {
    id: 'J08-13',
    formId: 'J08',
    questionNumber: 13,
    categories: ['S'],
    difficulty: 2,
    topics: ['linear-equations', 'modeling'],
    question: 'Table shows engine size (x liters) and weight (w kg). Model: ŵ = 375x + 370. Difference between actual weight of smallest engine vehicle and predicted weight?',
    choices: ['79', '296', '370', '671'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Smallest engine: 2.0L, actual weight 1041 kg. Predicted: 375(2) + 370 = 1120. Difference: |1041 − 1120| = 79.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 14 - Domain of function
  // -------------------------------------------------------------------------
  {
    id: 'J08-14',
    formId: 'J08',
    questionNumber: 14,
    categories: ['F'],
    difficulty: 1,
    topics: ['function-notation', 'domain'],
    question: 'd is distance left to hike, t is minutes since start. Hike takes 13 minutes for 860m trail. What is domain of function?',
    choices: ['[0, 13]', '[0, 66²/₁₃]', '[0, 436½]', '[0, 860]'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Domain is the set of valid inputs (t values). Time goes from 0 to 13 minutes. Domain = [0, 13].',
    hasFigure: true,
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 15 - Complex number division
  // -------------------------------------------------------------------------
  {
    id: 'J08-15',
    formId: 'J08',
    questionNumber: 15,
    categories: ['N'],
    difficulty: 2,
    topics: ['complex-numbers'],
    question: 'Which number equals (10i + 2)/(2i)?',
    choices: ['5 − i', '6', '9i', '11i'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: '(10i + 2)/(2i) = 10i/(2i) + 2/(2i) = 5 + 1/i = 5 + 1/i × (−i)/(−i) = 5 + (−i)/(−i²) = 5 + (−i)/1 = 5 − i.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 16 - System of equations
  // -------------------------------------------------------------------------
  {
    id: 'J08-16',
    formId: 'J08',
    questionNumber: 16,
    categories: ['A'],
    difficulty: 2,
    topics: ['systems-of-equations'],
    question: 'What is x in the solution to: 8x + y = 6 and 9x − 6y = 11?',
    choices: ['25/39', '47/57', '57/47', '39/25'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'From first: y = 6 − 8x. Substitute: 9x − 6(6 − 8x) = 11. 9x − 36 + 48x = 11. 57x = 47. x = 47/57.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 17 - Sine function period and amplitude
  // -------------------------------------------------------------------------
  {
    id: 'J08-17',
    formId: 'J08',
    questionNumber: 17,
    categories: ['F'],
    difficulty: 2,
    topics: ['trig-functions', 'amplitude', 'period'],
    question: 'y = 2sin(6πx) for 0 ≤ x ≤ 1. What are period and amplitude?',
    choices: ['Period: 1/3, Amplitude: 2', 'Period: 1/3, Amplitude: 4', 'Period: 3, Amplitude: 2', 'Period: 3, Amplitude: 4'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Amplitude = |2| = 2. Period = 2π/(6π) = 1/3.',
    hasFigure: true,
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 18 - Solving literal equation
  // -------------------------------------------------------------------------
  {
    id: 'J08-18',
    formId: 'J08',
    questionNumber: 18,
    categories: ['A'],
    difficulty: 2,
    topics: ['solving-for-variable'],
    question: 'Let c, h, q be distinct nonzero real numbers. Solution to c(x − h) = q?',
    choices: ['q/c − h', 'q/c + h', 'q/c + h/c', 'q/h + c/h'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'c(x − h) = q. x − h = q/c. x = q/c + h.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 19 - Triangle inequality / angle-side relationship
  // -------------------------------------------------------------------------
  {
    id: 'J08-19',
    formId: 'J08',
    questionNumber: 19,
    categories: ['IES'],
    difficulty: 2,
    topics: ['triangles', 'triangle-inequality'],
    question: 'Triangle RST has RS = 18cm, ST = 12cm, RT = 27cm. Which statement is true?',
    choices: ['∠R is least', '∠S is least', '∠R is greatest', '∠T is greatest'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Largest angle is opposite longest side. Smallest angle opposite shortest side. ST = 12 is shortest, so ∠R (opposite ST) is smallest.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 20 - Inverse function
  // -------------------------------------------------------------------------
  {
    id: 'J08-20',
    formId: 'J08',
    questionNumber: 20,
    categories: ['F'],
    difficulty: 2,
    topics: ['inverse-functions'],
    question: 'Given f(x) = 2x − 5, what is f⁻¹(x)?',
    choices: ['(x − 5)/2', '(x + 5)/2', '(x + 2)/5', '½x + 5'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'y = 2x − 5. Swap: x = 2y − 5. Solve: x + 5 = 2y. y = (x + 5)/2.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 21 - Area of shaded region (circle in square)
  // -------------------------------------------------------------------------
  {
    id: 'J08-21',
    formId: 'J08',
    questionNumber: 21,
    categories: ['IES'],
    difficulty: 3,
    topics: ['circles', 'rectangles', 'area'],
    question: 'Square circumscribed about circle with 8-inch radius. Diameter AB parallel to 2 sides, CD is diagonal. Find total shaded area.',
    choices: ['64', '176', '224', '256'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Square side = 16 (diameter). Square area = 256. Circle area = 64π ≈ 201. Shaded region calculation involves the semicircle and triangle regions. Answer is approximately 224.',
    hasFigure: true,
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 22 - Dilation
  // -------------------------------------------------------------------------
  {
    id: 'J08-22',
    formId: 'J08',
    questionNumber: 22,
    categories: ['G'],
    difficulty: 2,
    topics: ['transformations', 'dilation'],
    question: 'Dilation maps (0,0) to itself and (4,−16) to (1,−4). Where does it map (12,−24)?',
    choices: ['(3,−6)', '(9,−36)', '(15,−36)', '(16,−20)'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Scale factor: (4,−16) → (1,−4) means k = 1/4. Apply to (12,−24): (12×¼, −24×¼) = (3,−6).',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 23 - Expected value
  // -------------------------------------------------------------------------
  {
    id: 'J08-23',
    formId: 'J08',
    questionNumber: 23,
    categories: ['S'],
    difficulty: 2,
    topics: ['expected-value'],
    question: 'Advertisement revenue: $400(0.02), $100(0.02), $60(0.04), $0(0.92). Expected value?',
    choices: ['$12.40', '$13.32', '$35.00', '$80.00'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'E = 400(0.02) + 100(0.02) + 60(0.04) + 0(0.92) = 8 + 2 + 2.40 + 0 = $12.40.',
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 24 - Normal distribution
  // -------------------------------------------------------------------------
  {
    id: 'J08-24',
    formId: 'J08',
    questionNumber: 24,
    categories: ['S'],
    difficulty: 2,
    topics: ['normal-distribution', 'probability-basic'],
    question: 'Normal distribution shown with 34% between 1000-1100 and 2% below 900. Find P(1100 < X < 1200).',
    choices: ['14%', '17%', '18%', '30%'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Using symmetry: 34% from mean to +1σ, 2% below −2σ means 14% from +1σ to +2σ (between 1100-1200).',
    hasFigure: true,
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 25 - Repeating decimal pattern
  // -------------------------------------------------------------------------
  {
    id: 'J08-25',
    formId: 'J08',
    questionNumber: 25,
    categories: ['IES'],
    difficulty: 2,
    topics: ['decimals', 'patterns'],
    question: 'Consider repeating decimal 0.7̄4̄1̄2̄. What is the 323rd digit right of decimal?',
    choices: ['1', '2', '4', '7'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Pattern "7412" repeats every 4 digits. 323 ÷ 4 = 80 remainder 3. Third digit in pattern is 1.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 26 - Trigonometry word problem
  // -------------------------------------------------------------------------
  {
    id: 'J08-26',
    formId: 'J08',
    questionNumber: 26,
    categories: ['G'],
    difficulty: 2,
    topics: ['trig-ratios', 'word-problems'],
    question: 'Jabari estimates 25° angle of elevation to tree top from 45 feet away. Expression for tree height?',
    choices: ['45 sin 25°', '45 cos 25°', '45 tan 25°', '45 cot 25°'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'tan(angle) = opposite/adjacent = height/45. Height = 45 tan 25°.',
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 27 - Cone volume word problem
  // -------------------------------------------------------------------------
  {
    id: 'J08-27',
    formId: 'J08',
    questionNumber: 27,
    categories: ['G'],
    difficulty: 2,
    topics: ['volume', 'cones', 'word-problems'],
    question: '1.5 lb bag of sugar leaked into cone shape: radius 3", height 2". Sugar weighs 0.025 lb/in³. How much left in bag?',
    choices: ['0.086', '1.029', '1.350', '1.475'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Cone volume = (π/3)r²h = (π/3)(9)(2) = 6π ≈ 18.85 in³. Leaked weight = 18.85 × 0.025 ≈ 0.471 lb. Remaining = 1.5 − 0.471 ≈ 1.029 lb.',
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 28 - Binomial expansion
  // -------------------------------------------------------------------------
  {
    id: 'J08-28',
    formId: 'J08',
    questionNumber: 28,
    categories: ['A'],
    difficulty: 3,
    topics: ['polynomial-expansion', 'binomial-theorem'],
    question: '(x + by)ⁿ expanded with 2nd term = 8x³y. Total number of terms?',
    choices: ['5', '8', '11', '12'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: '2nd term = nx^(n-1)(by) = nbx^(n-1)y = 8x³y. So n−1 = 3, meaning n = 4. Binomial of degree 4 has 5 terms.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 29 - Exponential vs linear model
  // -------------------------------------------------------------------------
  {
    id: 'J08-29',
    formId: 'J08',
    questionNumber: 29,
    categories: ['F'],
    difficulty: 2,
    topics: ['exponential-functions', 'modeling'],
    question: 'Medication concentration: t=0: 850 mg/L, t=6: 151 mg/L, t=12: 27 mg/L. Best model type and reason?',
    choices: ['Linear, equal factors', 'Linear, equal differences', 'Exponential, equal factors', 'Exponential, equal differences'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '151/850 ≈ 0.178, 27/151 ≈ 0.179. Equal ratios over equal intervals = exponential decay by equal factors.',
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 30 - Irrational number expression
  // -------------------------------------------------------------------------
  {
    id: 'J08-30',
    formId: 'J08',
    questionNumber: 30,
    categories: ['N'],
    difficulty: 3,
    topics: ['radicals', 'number-properties'],
    question: 'Given n is rational, which statement about (−4√14 − n)/2 must be true?',
    choices: ['It is positive', 'It is negative', 'It is irrational', 'It is rational'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: '√14 is irrational. −4√14 is irrational. Subtracting rational n gives irrational. Dividing by 2 gives irrational.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 31 - Hyperbola x-intercept
  // -------------------------------------------------------------------------
  {
    id: 'J08-31',
    formId: 'J08',
    questionNumber: 31,
    categories: ['G'],
    difficulty: 2,
    topics: ['conic-sections', 'hyperbola'],
    question: 'Graph of x²/9 − y²/49 = 1 has x-intercept at which point?',
    choices: ['(0,0)', '(3,0)', '(7,0)', '(9,0)'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'At x-intercept, y = 0: x²/9 = 1, so x² = 9, x = ±3. Point (3,0).',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 32 - Logarithmic function equation
  // -------------------------------------------------------------------------
  {
    id: 'J08-32',
    formId: 'J08',
    questionNumber: 32,
    categories: ['F'],
    difficulty: 3,
    topics: ['logarithms', 'function-transformations'],
    question: 'Which equation matches the graph? (Log function passing through approximately (4,0) and (6,4))',
    choices: ['y = 4(4)^(x−4) − 2', 'y = 4(4)^(x+2) + 4', 'y = 4log₄(x−4) − 2', 'y = 4log₄(x+2) + 4'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'The graph shows a logarithmic function. Testing y = 4log₄(x+2) + 4: at x = 2, y = 4log₄(4) + 4 = 4(1) + 4 = 8. Pattern matches.',
    hasFigure: true,
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 33 - Set theory (union/intersection)
  // -------------------------------------------------------------------------
  {
    id: 'J08-33',
    formId: 'J08',
    questionNumber: 33,
    categories: ['S'],
    difficulty: 2,
    topics: ['sets', 'counting'],
    question: 'W has 14 elements, X has 11 elements (none in W). Y = W ∩ X, Z = W ∪ X. Which ordering is true?',
    choices: ['|X| < |W| < |Y| < |Z|', '|X| < |W| < |Z| < |Y|', '|Y| < |X| < |W| < |Z|', '|Y| < |Z| < |X| < |W|'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Since X and W share no elements: |Y| = |W ∩ X| = 0. |Z| = |W ∪ X| = 14 + 11 = 25. Order: 0 < 11 < 14 < 25, so |Y| < |X| < |W| < |Z|.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 34 - Triangle area ratio with midpoints
  // -------------------------------------------------------------------------
  {
    id: 'J08-34',
    formId: 'J08',
    questionNumber: 34,
    categories: ['G'],
    difficulty: 3,
    topics: ['triangles', 'similar-triangles', 'area'],
    question: 'Isosceles △ABC with midpoints D, E, H on sides. F, G are midpoints of DH and EH. Ratio of area △ABC to area △FGH?',
    choices: ['4:1', '8:1', '16:1', '64:1'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Each midpoint triangle has 1/4 the area. △DEH has 1/4 area of △ABC. △FGH has 1/4 area of △DEH. Ratio = 1/(1/4 × 1/4) = 16:1.',
    hasFigure: true,
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 35 - Perpendicular lines slope product
  // -------------------------------------------------------------------------
  {
    id: 'J08-35',
    formId: 'J08',
    questionNumber: 35,
    categories: ['G'],
    difficulty: 1,
    topics: ['slope', 'perpendicular-lines'],
    question: 'Two perpendicular lines with nonzero slopes. What is product of their slopes?',
    choices: ['−2', '−1', '1', '2'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Perpendicular lines have slopes that are negative reciprocals. Their product is always −1.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 36 - Absolute value maximum
  // -------------------------------------------------------------------------
  {
    id: 'J08-36',
    formId: 'J08',
    questionNumber: 36,
    categories: ['IES'],
    difficulty: 2,
    topics: ['absolute-value', 'inequalities'],
    question: 'For integers m and n where −3 ≤ m ≤ 4 and −10 ≤ n ≤ 9, what is greatest value of |m − n|?',
    choices: ['5', '7', '12', '14'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Maximize |m − n| by maximizing distance: m = 4, n = −10. |4 − (−10)| = |14| = 14.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 37 - Factor theorem
  // -------------------------------------------------------------------------
  {
    id: 'J08-37',
    formId: 'J08',
    questionNumber: 37,
    categories: ['A'],
    difficulty: 2,
    topics: ['factoring', 'polynomial-zeros'],
    question: 'A factor of 2.5x² − 30x + c is (x + 4). What is c?',
    choices: ['−160', '−26', '80', '110'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'If (x + 4) is a factor, then f(−4) = 0. 2.5(16) − 30(−4) + c = 0. 40 + 120 + c = 0. c = −160.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 38 - Counting principle
  // -------------------------------------------------------------------------
  {
    id: 'J08-38',
    formId: 'J08',
    questionNumber: 38,
    categories: ['IES'],
    difficulty: 1,
    topics: ['counting', 'combinations'],
    question: 'Questionnaire has 5 questions, each with "yes" or "no". How many ways can employee answer all 5?',
    choices: ['7', '25', '32', '64'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '2 choices per question, 5 questions. Total = 2⁵ = 32.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 39 - Percent relationships
  // -------------------------------------------------------------------------
  {
    id: 'J08-39',
    formId: 'J08',
    questionNumber: 39,
    categories: ['IES'],
    difficulty: 3,
    topics: ['percent', 'solving-for-variable'],
    question: '40% of m equals a, and b = 7a/10. Which expression gives 112% of m in terms of b?',
    choices: ['4b', '12b', '40b', '84b'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'a = 0.4m, so m = a/0.4 = 2.5a. b = 0.7a, so a = b/0.7. m = 2.5(b/0.7) = 25b/7. 112% of m = 1.12 × 25b/7 = 28b/7 = 4b.',
    isScored: true,
    isModeling: false
  },

  // -------------------------------------------------------------------------
  // QUESTION 40 - Sum of integers formula
  // -------------------------------------------------------------------------
  {
    id: 'J08-40',
    formId: 'J08',
    questionNumber: 40,
    categories: ['N'],
    difficulty: 2,
    topics: ['arithmetic-sequences', 'summation'],
    question: 'Sum of first 15 positive integers is 120. What is sum of first 75 positive integers?',
    choices: ['600', '1,140', '2,775', '2,850'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Sum formula: n(n+1)/2. For n=75: 75(76)/2 = 5700/2 = 2850.',
    isScored: true,
    isModeling: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 41 - Rational function asymptotes
  // -------------------------------------------------------------------------
  {
    id: 'J08-41',
    formId: 'J08',
    questionNumber: 41,
    categories: ['F'],
    difficulty: 3,
    topics: ['rational-expressions', 'asymptotes'],
    question: 'What are all asymptotes of y = 3x²/(x² − 9)?',
    choices: ['x = −3 and y = 3', 'x = 3 and y = 0', 'x = −3, x = 3, and y = 0', 'x = −3, x = 3, and y = 3'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Vertical asymptotes where denominator = 0: x² − 9 = 0, x = ±3. Horizontal: degrees equal, so y = 3/1 = 3.',
    isScored: true,
    isModeling: false
  }
];

// =============================================================================
// CATEGORY SUMMARY
// =============================================================================

export const CATEGORY_COUNTS_J08 = {
  'A': 9,  // Algebra
  'F': 8,  // Functions
  'G': 8,  // Geometry
  'N': 5,  // Number & Quantity
  'S': 6,  // Statistics & Probability
  'IES': 8 // Integrating Essential Skills
};

// Modeling questions (cross-cutting, tracked separately)
export const MODELING_COUNT_J08 = 11;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function getScaleScore(rawScore) {
  const entry = SCALE_SCORE_TABLE_J08.find(e => e.raw === rawScore);
  return entry ? entry.scale : null;
}

export function getQuestionsByCategory(categoryCode) {
  return questions_J08.filter(q => q.categories.includes(categoryCode));
}

export function getModelingQuestions() {
  return questions_J08.filter(q => q.isModeling);
}

export default {
  metadata: FORM_METADATA,
  scaleTable: SCALE_SCORE_TABLE_J08,
  questions: questions_J08,
  categoryCounts: CATEGORY_COUNTS_J08,
  getScaleScore,
  getQuestionsByCategory,
  getModelingQuestions
};
